/**
 * @file Текущий пользователь и пароль шифрования, только в памяти приложения.
 *
 * При открытии страницы восстанавливаем сессию через API. Ушедший эффект
 * не может опубликовать поздний ответ, в том числе при проверке StrictMode.
 */
import { useEffect, useState } from 'react'

import { deleteSession, fetchSession } from '../features/session/api'
import { CustomerContext, type CustomerInfo } from './useCustomer'

export const CustomerProvider = ({
    children,
    restoreSession = true,
}: {
    children: React.ReactNode
    // Выбирается при запуске: false передаётся для входа по OTAT.
    restoreSession?: boolean
}) => {
    const [customer, setCustomer] = useState<CustomerInfo | null>(null)
    // isLoaded означает завершение начальной проверки сессии, а не успешный вход.
    // Для OTAT этой проверки нет: сразу разрешаем показать экран обмена токена.
    const [isLoaded, setIsLoaded] = useState(!restoreSession)
    const [password, setPassword] = useState<string | null>(null)

    useEffect(() => {
        // Удаляем запись, оставшуюся от предыдущих версий. Новых записей нет;
        // недоступность storage не должна мешать восстановлению сессии через API.
        try {
            localStorage.removeItem('customer')
        } catch {
            /* Storage может быть запрещён настройками браузера. */
        }

        // OTAT сам создаёт и проверяет сессию. Старый GET не должен
        // перезаписать результат входа или отправить пользователя в dashboard.
        if (!restoreSession) return

        const controller = new AbortController()
        const signal = AbortSignal.any([
            controller.signal,
            AbortSignal.timeout(30_000),
        ])

        void fetchSession(signal)
            .then((value) => {
                if (!controller.signal.aborted) setCustomer(value)
            })
            .catch(() => {
                if (!controller.signal.aborted) setCustomer(null)
            })
            .finally(() => {
                if (!controller.signal.aborted) setIsLoaded(true)
            })

        return () => controller.abort()
    }, [restoreSession])

    async function deleteCustomerSession() {
        try {
            await deleteSession(AbortSignal.timeout(30_000))
            setCustomer(null)
            setPassword(null)
        } catch (error) {
            console.error('Failed to delete customer session:', error)
        }
    }

    return (
        <CustomerContext.Provider
            value={{
                customer,
                setCustomer,
                deleteCustomerSession,
                isLoaded,
                password,
                setPassword,
            }}
        >
            {children}
        </CustomerContext.Provider>
    )
}
